<template>
  <div class="global-fab">
    <!-- Speed Dial Menu with FAB as activator -->
    <v-menu
      v-model="showMenu"
      location="top end"
      :close-on-content-click="true"
      offset="16"
    >
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          icon="mdi-plus"
          color="primary"
          size="large"
          class="fab-button"
          elevation="8"
        />
      </template>
      <v-list density="compact" class="pa-0" rounded>
        <v-list-item
          prepend-icon="mdi-lightbulb-on"
          title="Новая идея"
          @click="openIdeaDialog"
        />
        <v-list-item
          prepend-icon="mdi-checkbox-marked-outline"
          title="Новая задача"
          @click="openTaskDialog"
        />
      </v-list>
    </v-menu>

    <!-- Create Idea Dialog -->
    <v-dialog v-model="ideaDialog" max-width="700" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-lightbulb-on</v-icon>
          Новая идея
        </v-card-title>
        <v-card-text>
          <v-form ref="ideaFormRef" v-model="ideaFormValid">
            <v-text-field
              v-model="ideaForm.title"
              label="Заголовок *"
              :rules="[v => !!v || 'Заголовок обязателен']"
              variant="outlined"
              class="mb-3"
            />

            <MarkdownEditor
              v-model="ideaForm.description"
              placeholder="Опишите проблему или предложение подробнее (поддерживается Markdown)"
              :collaborative="false"
              entity-type="idea"
              entity-id="new"
              field="description"
              min-height="200px"
              class="mb-3"
            />

            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="ideaForm.type"
                  :items="typeOptions"
                  label="Тип"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="ideaForm.priority"
                  :items="priorityOptions"
                  label="Приоритет"
                  variant="outlined"
                  clearable
                />
              </v-col>
            </v-row>

            <v-autocomplete
              v-model="ideaForm.projectIds"
              :items="projects"
              item-title="title"
              item-value="id"
              label="Проекты"
              variant="outlined"
              clearable
              multiple
              chips
              closable-chips
              class="mb-3"
              :hint="contextHint"
              persistent-hint
            />

            <!-- Related Task: auto-detected from context or manual search -->
            <v-alert
              v-if="ideaForm.relatedTaskId && relatedTaskDisplay"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              <div class="d-flex align-center">
                <span>Связана с задачей: {{ relatedTaskDisplay }}</span>
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  class="ml-2"
                  @click="clearTaskContext"
                >
                  <v-icon size="small">mdi-close</v-icon>
                </v-btn>
              </div>
            </v-alert>
            <v-autocomplete
              v-if="!ideaForm.relatedTaskId"
              v-model="ideaForm.relatedTaskId"
              :items="taskSearchResults"
              item-title="displayTitle"
              item-value="id"
              label="Связать с задачей"
              variant="outlined"
              clearable
              class="mb-3"
              :loading="taskSearchLoading"
              :no-data-text="taskSearchQuery ? 'Задачи не найдены' : 'Введите текст для поиска'"
              @update:search="onTaskSearch"
              @update:model-value="onTaskSelected"
            />

            <v-combobox
              v-model="ideaForm.tags"
              label="Теги"
              variant="outlined"
              chips
              closable-chips
              multiple
              hint="Введите тег и нажмите Enter"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="ideaDialog = false">Отмена</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!ideaFormValid"
            @click="saveIdea"
          >
            Создать
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for errors -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000" location="bottom end">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ideasApi,
  projectsApi,
  tasksApi,
  type IdeaType,
  type IdeaPriority,
} from '@/plugins/api'
import MarkdownEditor from '@/components/MarkdownEditor.vue'
import { useLookups } from '@/composables/useLookups'
import { useTasksStore } from '@/stores/tasks'

const route = useRoute()
const router = useRouter()
const { projects } = useLookups()

// State
const showMenu = ref(false)
const ideaDialog = ref(false)
const saving = ref(false)

// Snackbar
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('error')

// Context from current page
const contextProjectIds = ref<string[]>([])
const contextTaskId = ref<string | null>(null)
const contextTaskTitle = ref<string | null>(null)

// Idea form
const ideaFormRef = ref()
const ideaFormValid = ref(false)
const ideaForm = reactive({
  title: '',
  description: '',
  type: 'other' as IdeaType,
  priority: undefined as IdeaPriority | undefined,
  projectIds: [] as string[],
  relatedTaskId: undefined as string | undefined,
  tags: [] as string[],
})

// Options
const typeOptions = [
  { title: 'Баг', value: 'bug' },
  { title: 'Улучшение', value: 'improvement' },
  { title: 'Фича', value: 'feature' },
  { title: 'Исследование', value: 'research' },
  { title: 'Вопрос', value: 'question' },
  { title: 'Другое', value: 'other' },
]

const priorityOptions = [
  { title: 'Низкий', value: 'low' },
  { title: 'Средний', value: 'medium' },
  { title: 'Высокий', value: 'high' },
]

// Task search for manual linking
const taskSearchResults = ref<Array<{ id: string; displayTitle: string }>>([])
const taskSearchLoading = ref(false)
const taskSearchQuery = ref('')
let taskSearchTimeout: ReturnType<typeof setTimeout> | null = null

const relatedTaskDisplay = computed(() => {
  if (contextTaskId.value && contextTaskTitle.value && ideaForm.relatedTaskId === contextTaskId.value) {
    return contextTaskTitle.value
  }
  const found = taskSearchResults.value.find(t => t.id === ideaForm.relatedTaskId)
  return found?.displayTitle || ideaForm.relatedTaskId
})

async function onTaskSearch(query: string) {
  taskSearchQuery.value = query
  if (taskSearchTimeout) clearTimeout(taskSearchTimeout)
  if (!query || query.length < 2) {
    taskSearchResults.value = []
    return
  }
  taskSearchTimeout = setTimeout(async () => {
    taskSearchLoading.value = true
    try {
      const response = await tasksApi.getAll({ search: query, limit: 20, _allDepartments: true })
      const tasks = response.data?.data || response.data?.items || []
      taskSearchResults.value = tasks.map((t: any) => ({
        id: t.id,
        displayTitle: t.humanId ? `${t.humanId} - ${t.title}` : t.title,
      }))
    } catch {
      taskSearchResults.value = []
    } finally {
      taskSearchLoading.value = false
    }
  }, 300)
}

function onTaskSelected(taskId: string | null) {
  if (taskId) {
    const task = taskSearchResults.value.find(t => t.id === taskId)
    if (task) {
      contextTaskTitle.value = task.displayTitle
    }
  }
}

// Computed
const contextHint = computed(() => {
  if (contextProjectIds.value.length > 0) {
    return 'Проекты предзаполнены из текущей страницы'
  }
  if (contextTaskId.value) {
    return 'Проекты будут унаследованы от связанной задачи'
  }
  return ''
})

// Watch route changes to detect context
watch(
  () => route.path,
  () => {
    detectContext()
  },
  { immediate: true }
)

// Detect context from current route
async function detectContext() {
  contextProjectIds.value = []
  contextTaskId.value = null
  contextTaskTitle.value = null

  // On project page
  if (route.path.startsWith('/projects/') && route.params.slug) {
    try {
      const response = await projectsApi.getBySlug(route.params.slug as string)
      if (response.data?.id) {
        contextProjectIds.value = [response.data.id]
      }
    } catch (e) {
      console.warn('Failed to detect project context:', e)
    }
  }

  // On task page — use SWR-cached tasks store (instant lookup, no API call)
  if (route.path.startsWith('/tasks/') && route.params.id) {
    const tasksStore = useTasksStore()
    const task = tasksStore.tasks.find(t => t.id === route.params.id)
    if (task) {
      contextTaskId.value = task.id
      contextTaskTitle.value = task.humanId
        ? `${task.humanId} - ${task.title}`
        : task.title
      if (task.taskProjects) {
        contextProjectIds.value = task.taskProjects.map(
          (tp: { projectId: string }) => tp.projectId
        )
      }
    } else {
      // Fallback: task not in store yet (cold start) — fetch from API
      try {
        const response = await tasksApi.getOne(route.params.id as string)
        if (response.data) {
          contextTaskId.value = response.data.id
          contextTaskTitle.value = response.data.humanId
            ? `${response.data.humanId} - ${response.data.title}`
            : response.data.title
          if (response.data.taskProjects) {
            contextProjectIds.value = response.data.taskProjects.map(
              (tp: { projectId: string }) => tp.projectId
            )
          }
        }
      } catch (e) {
        console.warn('Failed to detect task context:', e)
      }
    }
  }
}

function clearTaskContext() {
  contextTaskId.value = null
  contextTaskTitle.value = null
  ideaForm.relatedTaskId = undefined
  taskSearchResults.value = []
}

// Projects are provided by useLookups() composable
// with automatic WebSocket-driven refresh

// Dialog openers
function openIdeaDialog() {
  showMenu.value = false
  // Pre-fill from context
  ideaForm.title = ''
  ideaForm.description = ''
  ideaForm.type = 'other'
  ideaForm.priority = undefined
  ideaForm.projectIds = [...contextProjectIds.value]
  ideaForm.relatedTaskId = contextTaskId.value || undefined
  ideaForm.tags = []
  ideaDialog.value = true
}

function openTaskDialog() {
  showMenu.value = false
  // Navigate to task creation page with context
  const query: Record<string, string> = {}
  if (contextProjectIds.value[0]) {
    query.projectId = contextProjectIds.value[0]
  }
  if (contextTaskId.value) {
    query.relatedId = contextTaskId.value
  }
  router.push({ path: '/tasks/new', query })
}

// Save actions
async function saveIdea() {
  saving.value = true
  try {
    await ideasApi.create({
      title: ideaForm.title,
      description: ideaForm.description || undefined,
      type: ideaForm.type,
      priority: ideaForm.priority,
      projectIds: ideaForm.projectIds.length > 0 ? ideaForm.projectIds : undefined,
      relatedTaskId: ideaForm.relatedTaskId,
      tags: ideaForm.tags.length > 0 ? ideaForm.tags : undefined,
    })
    ideaDialog.value = false
    // Navigate to ideas page
    router.push('/ideas')
  } catch (error: any) {
    console.error('Failed to create idea:', error)
    const status = error?.response?.status
    if (status === 403) {
      snackbarText.value = 'Недостаточно прав для создания идей'
    } else if (status === 400) {
      const msg = error?.response?.data?.message
      snackbarText.value = Array.isArray(msg) ? msg.join(', ') : (msg || 'Ошибка валидации')
    } else {
      snackbarText.value = 'Не удалось создать идею. Попробуйте позже.'
    }
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    saving.value = false
  }
}

// useLookups() handles initial load and WS refresh via onMounted()
</script>

<style scoped>
.global-fab {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 1000;
}
</style>
